import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Square, Volume2, VolumeX } from "lucide-react";
import Layout from "../components/Layout";
import VoiceToggle from "../components/VoiceToggle";
import VoiceHelp from "../components/VoiceHelp";
import {
  useSpeechRecognition,
  getSRLang,
} from "../hooks/useSpeechRecognition";
import { useLanguage } from "../i18n/LanguageContext";
import { playTick, unlockAudio } from "../lib/audio";

const TEMPOS = [100, 120, 140, 160];

export default function Metronome() {
  const { t, lang } = useLanguage();
  const [bpm, setBpm] = useState(120);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [beatCount, setBeatCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [pulse, setPulse] = useState(0);
  const [voice, setVoice] = useState(false);

  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(null);

  const startAction = useCallback(() => {
    unlockAudio();
    setRunning(true);
  }, []);
  const stopAction = useCallback(() => setRunning(false), []);
  const startStop = useCallback(() => {
    setRunning((prev) => {
      const next = !prev;
      if (next) unlockAudio();
      return next;
    });
  }, []);

  const { supported: voiceSupported, listening } = useSpeechRecognition({
    enabled: voice,
    lang: getSRLang(lang),
    onStart: startAction,
    onStop: stopAction,
  });

  useEffect(() => {
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      intervalRef.current = null;
      timerRef.current = null;
      return;
    }

    setBeatCount(0);
    setElapsed(0);
    startRef.current = Date.now();

    const intervalMs = 60000 / bpm;
    let beat = 0;

    const tick = () => {
      beat += 1;
      setBeatCount(beat);
      setPulse((p) => p + 1);
      if (!muted) playTick(beat % 4 === 1);
    };

    // Immediate first tick
    tick();
    intervalRef.current = setInterval(tick, intervalMs);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 250);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [running, bpm, muted]);

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <Layout showBack>
      <div className="vls-page" data-testid="metronome-page">
        <h1 className="vls-page-title">
          <span className="vls-grad-cyan">{t("metronome")}</span>
        </h1>

        <div className="vls-tempo-row" data-testid="tempo-row">
          {TEMPOS.map((b) => (
            <button
              key={b}
              type="button"
              className={`vls-tempo-btn ${bpm === b ? "active" : ""}`}
              onClick={() => setBpm(b)}
              data-testid={`tempo-${b}`}
              disabled={running}
            >
              <span className="vls-tempo-num">{b}</span>
              <span className="vls-tempo-unit">{t("bpm")}</span>
            </button>
          ))}
        </div>

        <div className="vls-metronome-visual" data-testid="metronome-visual">
          <div
            key={pulse}
            className={`vls-pulse-ring ${running ? "vls-pulse-anim" : ""}`}
            style={{ animationDuration: `${60 / bpm}s` }}
          >
            <div className="vls-pulse-core">
              <span className="vls-pulse-bpm">{bpm}</span>
              <span className="vls-pulse-unit">{t("bpm")}</span>
            </div>
          </div>
        </div>

        <div className="vls-stats" data-testid="metronome-stats">
          <div className="vls-stat">
            <span className="vls-stat-label">{t("elapsedTime")}</span>
            <span className="vls-stat-value" data-testid="stat-elapsed">{fmtTime(elapsed)}</span>
          </div>
          <div className="vls-stat">
            <span className="vls-stat-label">{t("beats")}</span>
            <span className="vls-stat-value" data-testid="stat-beats">{beatCount}</span>
          </div>
        </div>

        <div className="vls-controls">
          <button
            type="button"
            className={`vls-btn-primary ${running ? "stop" : ""}`}
            onClick={startStop}
            data-testid="start-stop-button"
          >
            {running ? <Square size={22} /> : <Play size={22} />}
            <span>{running ? t("stop") : t("start")}</span>
          </button>
          <button
            type="button"
            className="vls-btn-secondary"
            onClick={() => setMuted((m) => !m)}
            data-testid="mute-button"
            aria-label={muted ? t("soundOff") : t("soundOn")}
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <VoiceToggle
            enabled={voice}
            setEnabled={setVoice}
            supported={voiceSupported}
            listening={listening}
          />
        </div>

        {!running && (
          <VoiceHelp supported={voiceSupported} />
        )}
      </div>
    </Layout>
  );
}
